/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
  Spinner,
  Center,
  SimpleGrid,
  Badge,
  Button,
  useToast,
} from "@chakra-ui/react";
import { FaBell, FaEnvelope, FaCommentDots } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import PageContainer from "../components/PageContainer";
import UnifiedCard from "../components/UnifiedCard";
import WhatsLayout from "../layouts/WhatsLayout";
import { useUI } from "../theme/ThemeProvider";
import Swal from "sweetalert2";
export default function Notifications() {
  const { i18n } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq").replace(/\/+$/, '');

  // Fetch notifications from API (GET)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);

        // Use proxy in development, full URL in production


        const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

        // Fetch notifications using TenantnNotifications API (GET /TenantnNotifications?tenantId=-1)
        const res = await fetch(`${baseUrl}/Storereview/GetStoreReviews?tenantId=${tenantId}`, {
          method: "GET",
          headers: {
            "Accept": "application/json",
          },
        });

        // Try to parse response
        let data = [];
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            data = await res.json();
          } catch (parseError) {
            const text = await res.text();
            console.error("Failed to parse JSON response:", parseError);
            console.error("Response text:", text);
            if (text) {
              try {
                data = JSON.parse(text);
              } catch {
                data = [];
              }
            }
          }
        } else {
          const text = await res.text();
          if (text) {
            try {
              data = JSON.parse(text);
            } catch {
              data = [];
            }
          }
        }

        if (res.ok && Array.isArray(data) && data.length > 0) {
          // Map API data to notification format
          const mappedNotifications = data.map((r) => {
            const id = r.id ?? r.Id;
            const tenantId = r.tenantId ?? r.TenantId;

            const customerName = r.customerName ?? r.CustomerName ?? "";
            const rating = r.rating ?? r.Rating ?? 0;
            const content = r.description ?? r.Description ?? "";
            const hasImage = r.hasImage ?? r.HasImage ?? false;
            const imageUrl = r.imageUrl ?? r.ImageUrl ?? "";
            const isActive = r.isActive ?? r.IsActive;

            const createDateRaw = r.createDate ?? r.CreateDate; // ✅ احتفظ بالنص الأصلي
            const dateObj = createDateRaw ? new Date(createDateRaw) : new Date();

            return {
              id,
              tenantId,
              rating,
              content,
              date: dateObj,            // للعرض فقط
              createDateRaw,            // ✅ للإرسال كما هو
              imageUrl: hasImage ? imageUrl : "",
              customerName,
              isActive,
              title: customerName || (isArabic ? "زبون" : "Customer"),
              type: "review",
            };
          });



          setNotifications(mappedNotifications);
          console.log("✅ Notifications loaded from API:", mappedNotifications);
        } else {
          console.warn("No notifications found or API error:", res?.status || "Unknown");
          setNotifications([]);
        }
      } catch (err) {
        console.error("Error fetching notifications:", err);
        setNotifications([]);

        toast({
          title: isArabic ? "فشل التحميل" : "Load Failed",
          description: isArabic
            ? "فشل تحميل الإشعارات. يرجى المحاولة لاحقاً."
            : "Failed to load notifications. Please try again later.",
          status: "error",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [baseUrl, isArabic, toast]);

  const updateNotificationStatus = async (review, isActive) => {
    try {
      const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

      const payload = {
        id: Number(review.id),                 // ✅ نفس ID
        tenantId: Number(tenantId),

        customerName: review.customerName || "",
        hasImage: !!review.imageUrl,
        imageUrl: review.imageUrl || "",

        rating: Number(review.rating || 0),
        description: review.content || "",

        // ✅ مهم جداً: ابعث نفس CreateDate الأصلي كنص ISO
        createDate: review.createDateRaw
          ? new Date(review.createDateRaw).toISOString()
          : (review.date?.toISOString?.() || new Date().toISOString()),

        // ✅ فقط هذا الذي يتغير
        isActive: Number(isActive),
      };

      const res = await fetch(`${baseUrl}/Storereview/AddStoreReviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || "Update failed");
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === review.id ? { ...n, isActive } : n))
      );

      Swal.fire({
        icon: "success",
        title: isArabic
          ? (isActive === 1 ? "تم قبول التقييم" : "تم رفض التقييم")
          : (isActive === 1 ? "Review approved" : "Review rejected"),
        timer: 1500,
        showConfirmButton: false,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: isArabic ? "فشل تحديث التقييم" : "Failed to update",
        text: e?.message,
        background: isDark ? "#1E293B" : "#fff",
        color: isDark ? "#fff" : "#000",
      });
    }
  };



  return (
    <PageContainer title={isArabic ? "مراجعة تقييمات المتجر" : "Store Reviews Approval"}>
      {loading ? (
        <Center minH="60vh">
          <Spinner color={colors.mainColor} size="xl" />
        </Center>
      ) : notifications.length === 0 ? (
        <Center minH="60vh">
          <Text color={colors.subText} fontSize="lg">
            {isArabic ? "لا توجد إشعارات حالياً 🔕" : "No notifications currently 🔕"}
          </Text>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          {notifications.map((notif) => (
            <UnifiedCard
              key={notif.id}
              icon={notif.type === "message" ? FaEnvelope : FaCommentDots}
              title={notif.title}
            >
              <HStack mb={3} spacing={3}>
                {notif.isActive === 1 && (
                  <Badge colorScheme="green">{isArabic ? "مقبول" : "Approved"}</Badge>
                )}
                {notif.isActive === 0 && (
                  <Badge colorScheme="red">{isArabic ? "مرفوض" : "Rejected"}</Badge>
                )}
                {(notif.isActive === null || notif.isActive === undefined) && (
                  <Badge colorScheme="yellow">{isArabic ? "بانتظار المراجعة" : "Pending Review"}</Badge>
                )}
              </HStack>

              <HStack spacing={3}>
                <Button
                  size="sm"
                  colorScheme="green"
                  isDisabled={notif.isActive === 1}
                  onClick={() => updateNotificationStatus(notif, 1)}
                >
                  {isArabic ? "قبول" : "Approve"}
                </Button>

                <Button
                  size="sm"
                  colorScheme="red"
                  isDisabled={notif.isActive === 0}
                  onClick={() => updateNotificationStatus(notif, 0)}
                >
                  {isArabic ? "رفض" : "Reject"}
                </Button>
              </HStack>
              {notif.rating > 0 && (
                <HStack mb={3}>
                  <Text color={colors.gold || "#F2B441"} fontSize="lg">
                    {"★".repeat(notif.rating)}
                    {"☆".repeat(5 - notif.rating)}
                  </Text>
                </HStack>
              )}


              <Text color={colors.subText} mb={3}>
                {notif.content}
              </Text>

              <Divider my={3} borderColor={colors.dividerColor} />

              <HStack justify="space-between">
                <VStack align="start" spacing={1}>
                  <Text color={colors.subText} fontSize="sm">
                    🕓 {notif.date.toLocaleDateString()}
                  </Text>
                  {notif.merchantName && (
                    <Text color={colors.subText} fontSize="xs" opacity={0.8}>
                      {isArabic ? "من: " : "From: "}{notif.merchantName}
                    </Text>
                  )}
                </VStack>
              </HStack>
            </UnifiedCard>
          ))}
        </SimpleGrid>
      )}
    </PageContainer>
  );
}
